import { useState, useEffect } from 'react';
import axios from 'axios';
import { Sidebar } from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Loader2, TrendingUp, Trophy, Medal } from 'lucide-react';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarIcon } from 'lucide-react';
import { formatCurrency, cn } from '../lib/utils';
import { format } from 'date-fns';
import { ScoreBar } from '../components/ScoreDisplay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RankingsPage() {
  const [loading, setLoading] = useState(true);
  const [rankings, setRankings] = useState([]);
  const [period, setPeriod] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  useEffect(() => {
    fetchRankings();
  }, [period, selectedDate]);

  const fetchRankings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rankings`, {
        params: {
          date: format(selectedDate, 'yyyy-MM-dd'),
          period
        }
      });
      setRankings(res.data.rankings || []);
    } catch (error) {
      console.error('Failed to fetch rankings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-orange-500" />;
    return null;
  };

  const getRankBgClass = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200';
    if (rank === 2) return 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200';
    if (rank === 3) return 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200';
    return 'bg-white border-slate-200';
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50">
      <Sidebar />
      
      <main className="flex-1 p-4 lg:p-8" data-testid="rankings-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit']">
            Location Rankings
          </h1>
          <p className="text-slate-600 mt-1">Track performance across all locations</p>
        </div>

        {/* Controls */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              <Tabs value={period} onValueChange={setPeriod}>
                <TabsList>
                  <TabsTrigger value="daily" data-testid="rankings-tab-daily">Daily</TabsTrigger>
                  <TabsTrigger value="weekly" data-testid="rankings-tab-weekly">Weekly</TabsTrigger>
                  <TabsTrigger value="monthly" data-testid="rankings-tab-monthly">Monthly</TabsTrigger>
                </TabsList>
              </Tabs>

              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" data-testid="rankings-date-btn">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(selectedDate, "PPP")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => {
                      if (date) {
                        setSelectedDate(date);
                        setDatePickerOpen(false);
                      }
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </CardContent>
        </Card>

        {/* Rankings List */}
        <Card data-testid="rankings-list">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-violet-600" />
              {period.charAt(0).toUpperCase() + period.slice(1)} Rankings
            </CardTitle>
            <CardDescription>
              Performance standings for {format(selectedDate, "MMMM d, yyyy")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
              </div>
            ) : rankings.length === 0 ? (
              <div className="text-center py-12">
                <Trophy className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No ranking data available for this period</p>
              </div>
            ) : (
              <div className="space-y-4">
                {rankings.map((location, index) => (
                  <div 
                    key={location.location_id}
                    className={cn(
                      "flex items-center gap-4 p-5 rounded-xl border transition-all hover:shadow-md",
                      getRankBgClass(location.rank)
                    )}
                    data-testid={`ranking-item-${location.location_id}`}
                  >
                    {/* Rank */}
                    <div className="flex items-center justify-center w-12">
                      {getRankIcon(location.rank) || (
                        <span className="text-2xl font-bold text-slate-400 font-mono">
                          {location.rank}
                        </span>
                      )}
                    </div>
                    
                    {/* Location Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold text-slate-900">{location.location_name}</h4>
                        <Badge variant="outline" className="text-xs">{location.region}</Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                        <span>{location.entry_count} {location.entry_count === 1 ? 'entry' : 'entries'}</span>
                        <span className="text-slate-300">•</span>
                        <span className="font-mono">{formatCurrency(location.total_sales)} sales</span>
                      </div>
                    </div>
                    
                    {/* Score */}
                    <div className="flex flex-col items-end gap-2">
                      <div className={cn(
                        "w-16 h-16 rounded-xl flex items-center justify-center font-mono font-bold text-xl text-white",
                        location.average_score >= 85 ? 'bg-emerald-500' :
                        location.average_score >= 70 ? 'bg-lime-500' :
                        location.average_score >= 55 ? 'bg-amber-500' : 'bg-red-500'
                      )}>
                        {Math.round(location.average_score)}
                      </div>
                      <div className="hidden sm:block w-24">
                        <ScoreBar score={location.average_score} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
